import './index.scss';
import '@pulsarml/avalon-ui/dist/style.css';
import 'react-toastify/dist/ReactToastify.css';
import * as serviceWorker from './serviceWorker';
import ApiClient from './api/index';
import Favicon from './components/favicon';
import { Provider } from 'react-redux';
import React from 'react';
import ReactDOM from 'react-dom/client';
import ReduxToastr from './utils/redux-toastr';
import api from './api/methods';
import store from './store';
import Amplify from 'aws-amplify';
import * as Sentry from '@sentry/react';
import awsconfig from './aws-exports';
import { ToastContainer } from 'react-toastify';
import { I18n, syncTranslation } from '@utils/i18n';
import { registerAllServices } from '_clean-code/services/context/register';
import { initiateMirage } from '@utils/networkMocking';
import { ToastProvider } from '@pulsarml/ui.react.components';
import App from './app';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/es';
import 'moment/locale/pt';
import 'moment/locale/en-gb';
import { Toast } from '@pulsarml/avalon-ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  PUBLIC_POSTHOG_KEY,
  PUBLIC_POSTHOG_HOST
} from './constants/posthog.js';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import { appChannel, appVersion } from './constants/config';
import {
  shouldLogErrorInSentry,
  shouldThrottleError
} from '_clean-code/utils/errorHandling';
import UserGuidingScript from '@clean-code/components/plugins/UserGuidingScript';
import { dropBuiltInEventsOnTvMode } from '_clean-code/analytics/tvMode';
// Polyfills for Samsung TV (Tizen 5.0 / Chrome 63)
if (typeof structuredClone === 'undefined') {
  (window as any).structuredClone = (obj: unknown) =>
    JSON.parse(JSON.stringify(obj));
}
if (typeof queueMicrotask === 'undefined') {
  (window as any).queueMicrotask = (fn: () => void) =>
    Promise.resolve().then(fn);
}
if (typeof globalThis === 'undefined') {
  (window as any).globalThis = window;
}

// Only production (stable and canary) is tracked. Dev, stage and PR-preview
// builds are also `import.meta.env.PROD` builds, so check the target instead.
const isProdDeploy = import.meta.env.VITE_APP_ENV === 'prod';

if (isProdDeploy) {
  posthog.init(PUBLIC_POSTHOG_KEY, {
    api_host: PUBLIC_POSTHOG_HOST,
    autocapture: false,
    // We don't use PostHog feature flags. This stops flag evaluation, which is
    // what's metered as feature-flag requests and was draining our quota.
    // The /flags endpoint keeps running for other config, so session recording
    // is NOT affected. Side effect: PostHog surveys are also disabled, since
    // they rely on feature flags internally (we don't use them).
    // (Note: do NOT use advanced_disable_flags here — that kills the /flags
    // request entirely and would disable session recording too.)
    advanced_disable_feature_flags: true,
    before_send: dropBuiltInEventsOnTvMode
  });
  // Lets canary and stable usage be compared during a canary rollout.
  posthog.register({ channel: appChannel });
}

const queryClient = new QueryClient();

// Moment locale names used by the MUI date pickers' AdapterMoment.
const MUI_ADAPTER_LOCALE: Record<string, string> = {
  us: 'en-gb',
  es: 'es',
  pt: 'pt'
};

/** Configure amplify */
Amplify.configure({
  ...awsconfig,
  oauth: {
    ...awsconfig.oauth,
    redirectSignIn: window.location.origin + awsconfig.oauth.redirectSignIn,
    redirectSignOut: window.location.origin + awsconfig.oauth.redirectSignOut
  }
});

if (isProdDeploy) {
  // Sentry error tracking
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error']
      })
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, //  Capture 10% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    release: `web-frontend@${appVersion}`,
    beforeSend: (event, hint) => {
      const error = hint?.originalException;
      if (
        error instanceof Error &&
        (!shouldLogErrorInSentry(error) || shouldThrottleError(error))
      ) {
        return null;
      }
      return event;
    }
  });
  // A canary only widens if its errors are no worse than stable's.
  Sentry.setTag('channel', appChannel);
}

// Test with local mock data
if (import.meta.env.VITE_APP_WITH_MOCK_DATA === 'true') {
  initiateMirage();
}

registerAllServices();

const render = () => {
  // SMOKY BLACK
  // #100B00
  // ST. PATRICK'S BLUE
  // #274C77
  // GLAUCOUS
  // #6279B8
  // BABY BLUE EYES
  // #A3CEF1
  // SHINY SHAMROCK
  // #4C9F70
  const fallbackColor = '#4bd1a0';

  const setCssVariable = (name: string, value: string) => {
    if (document.documentElement) {
      document.documentElement.style.setProperty(name, value);
    }
  };

  setCssVariable('--primary', fallbackColor);
  setCssVariable('--primary-dark', fallbackColor);
  setCssVariable('--primary-light', fallbackColor);

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      adapterLocale={MUI_ADAPTER_LOCALE[I18n._locale] ?? 'es'}
    >
      <PostHogProvider client={posthog}>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <UserGuidingScript />
              <App />
            </ToastProvider>
            <ToastContainer enableMultiContainer style={{ zIndex: 999999 }} />
            {/* Stop Settings V2's toasts (utils/toast.tsx) render here instead
                of the default container above - their message length varies
                a lot ("X uploaded successfully" vs a full cause name), and
                the default container's fixed 320px width wraps or clips
                longer ones. An auto-width container lets each toast size to
                its own content and stay on one line, without changing the
                320px default every other toast in the app still uses.
                enableMultiContainer is required whenever >1 ToastContainer
                is mounted - without it react-toastify paints the same toast
                into every container (F4 overlap duplicates). */}
            <ToastContainer
              enableMultiContainer
              containerId="stop-settings-v2"
              style={{ zIndex: 999999, width: 'auto' }}
            />
            <ReduxToastr />
            <Toast duration={3000} />
            <Favicon />
          </QueryClientProvider>
        </Provider>
      </PostHogProvider>
    </LocalizationProvider>
  );
};

serviceWorker.unregister();

ApiClient.injectApiMethods(api);

syncTranslation(store.getState().i18n.locale);

render();
